import React from 'react';
import { Hash } from 'lucide-react';
import { SettingsCard, InfoRow } from '../ui';
import { ExtendedBusinessDetails } from '../utils';
import { useBusinessStats } from '@/hooks/analytics/useBusinessStats';
import { useGooglePlacesData } from '@/hooks/business/useGooglePlacesData';

interface BusinessInformationDisplayProps {
  businessDetails: ExtendedBusinessDetails;
}

export const BusinessInformationDisplay: React.FC<BusinessInformationDisplayProps> = ({
  businessDetails,
}) => {
  // Use cached Google Places data hook instead of direct API call
  const {
    data: googlePlacesData,
    loading: loadingGooglePlaces
  } = useGooglePlacesData(businessDetails.google_place_id);

  const { stats, loading: statsLoading } = useBusinessStats(businessDetails.id, businessDetails.created_at, null);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatRatingChange = () => {
    if (businessDetails.initial_google_rating !== null && businessDetails.average_rating !== null) {
      const change = businessDetails.average_rating - businessDetails.initial_google_rating;
      const changeStr = change > 0 ? `+${change.toFixed(1)}` : change.toFixed(1);
      const colorClass = change > 0 ? 'text-green-600 ' : 
                        change < 0 ? 'text-red-600 ' : 
                        'text-gray-600 ';
      return { text: changeStr, className: colorClass };
    }
    return { text: 'N/A', className: '' };
  };

  const ratingChange = formatRatingChange();

  return (
    <SettingsCard 
      icon={Hash} 
      title="Business Information" 
      description="Technical information and metadata about your business"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">
            Basic Information
          </h4>
          <div className="space-y-3">
            <div className="flex flex-col space-y-1">
              <span className="text-sm font-medium text-foreground">Business ID:</span>
              <code className="text-xs bg-muted p-2 rounded font-mono text-muted-foreground break-all">
                {businessDetails.id}
              </code>
            </div>
            
            <div className="flex flex-col space-y-1">
              <span className="text-sm font-medium text-foreground">Owner ID:</span>
              <code className="text-xs bg-muted p-2 rounded font-mono text-muted-foreground break-all">
                {businessDetails.user_id || 'N/A'}
              </code>
            </div>
            
            <InfoRow 
              label="Created" 
              value={formatDate(businessDetails.created_at)} 
            />
            
            <InfoRow 
              label="Days Active" 
              value={statsLoading ? 'Loading...' : `${stats.daysActive} days`} 
            />
            
            <InfoRow 
              label="Google Maps" 
              value={businessDetails.google_place_id ? 'Integrated' : 'Not integrated'} 
            />
          </div>
        </div>

        {/* Business Activity */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">
            Business Activity
          </h4>
          <div className="space-y-3">
            <InfoRow 
              label="Current Reviews" 
              value={businessDetails.total_reviews || 0} 
            />
            
            <InfoRow 
              label="Current Rating" 
              value={businessDetails.average_rating ? `${businessDetails.average_rating.toFixed(1)}/5` : 'N/A'} 
            />
            
            {/* Show growth metrics if initial data exists */}
            {businessDetails.initial_google_data_captured_at && (
              <>
                <InfoRow 
                  label="Reviews Growth" 
                  value={businessDetails.initial_google_review_count !== null && businessDetails.total_reviews !== null
                    ? `+${businessDetails.total_reviews - businessDetails.initial_google_review_count}`
                    : 'N/A'
                  } 
                />
                
                <InfoRow 
                  label="Rating Change" 
                  value={ratingChange.text}
                  valueClassName={ratingChange.className}
                />
              </>
            )}
            
            <InfoRow 
              label="Last Review" 
              value={statsLoading ? 'Loading...' : (
                stats.lastReviewDate ? 
                  new Date(stats.lastReviewDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) 
                  : 'None yet'
              )} 
            />
            
            <InfoRow 
              label="Employee Count" 
              value={statsLoading ? 'Loading...' : stats.employeeCount} 
            />
          </div>
        </div>

        {/* Features & Activity */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">
            Features & Stats
          </h4>
          <div className="space-y-3">
            <InfoRow 
              label="Loyalty Programs" 
              value={statsLoading ? 'Loading...' : stats.loyaltyProgramsCount} 
            />
            
            <InfoRow 
              label="Job Postings" 
              value={statsLoading ? 'Loading...' : stats.jobPostingsCount} 
            />
            
            <InfoRow 
              label="Total Shifts" 
              value={statsLoading ? 'Loading...' : stats.totalShiftsCount} 
            />
            
            <InfoRow 
              label="This Month" 
              value={statsLoading ? 'Loading...' : stats.thisMonthShiftsCount} 
            />
            
            <InfoRow 
              label="QR Codes" 
              value={statsLoading ? 'Loading...' : stats.qrCodesCount} 
            />
          </div>
        </div>

        {/* System Status - spans full width on smaller screens */}
        <div className="space-y-3 md:col-span-2 lg:col-span-1">
          <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">
            System Status
          </h4>
          <div className="space-y-3">
            <InfoRow 
              label="Google Business" 
              value={businessDetails.gbp_linked_email ? 'Linked' : 'Not linked'} 
            />
            
            <InfoRow 
              label="Rewards" 
              value={businessDetails.reward_enabled ? 'Enabled' : 'Disabled'} 
            />
            
            <InfoRow 
              label="First Hire" 
              value={statsLoading ? 'Loading...' : (
                stats.firstHireDate ? 
                  new Date(stats.firstHireDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) 
                  : 'None yet'
              )} 
            />
            
            {businessDetails.google_place_id && (
              <InfoRow 
                label="Business Type" 
                value={statsLoading ? 'Loading...' : (
                  stats.businessCategory ? 
                    stats.businessCategory.charAt(0).toUpperCase() + stats.businessCategory.slice(1) 
                    : 'Not classified'
                )} 
              />
            )}
            
            {businessDetails.initial_google_data_captured_at && (
              <InfoRow 
                label="Baseline Captured" 
                value={formatDate(businessDetails.initial_google_data_captured_at)} 
              />
            )}
          </div>
        </div>

        {/* Google Reviews Data - matches existing card style */}
        {businessDetails.google_place_id && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">
              Google Reviews Data
            </h4>
            <div className="space-y-3">
              <InfoRow 
                label="Current Rating" 
                value={loadingGooglePlaces ? 'Loading...' : (
                  googlePlacesData ? `${googlePlacesData.rating.toFixed(1)}/5` : 'Failed to load'
                )} 
              />
              
              <InfoRow 
                label="Current Reviews" 
                value={loadingGooglePlaces ? 'Loading...' : (
                  googlePlacesData ? googlePlacesData.userRatingCount.toLocaleString() : 'Failed to load'
                )} 
              />
              
              <InfoRow 
                label="Baseline Rating" 
                value={businessDetails.initial_google_rating ? `${businessDetails.initial_google_rating.toFixed(1)}/5` : 'No baseline'} 
              />
              
              <InfoRow 
                label="Baseline Reviews" 
                value={businessDetails.initial_google_review_count ?? 'No baseline'} 
              />
              
              {/* Growth metrics - only show if we have both baseline and current data */}
              {businessDetails.initial_google_review_count !== null && 
               businessDetails.initial_google_rating !== null && 
               googlePlacesData && (
                <>
                  <InfoRow 
                    label="Reviews Growth" 
                    value={`+${googlePlacesData.userRatingCount - businessDetails.initial_google_review_count}`} 
                  />
                  
                  <InfoRow 
                    label="Rating Change" 
                    value={`${(googlePlacesData.rating - businessDetails.initial_google_rating) >= 0 ? '+' : ''}${(googlePlacesData.rating - businessDetails.initial_google_rating).toFixed(1)}`} 
                  />
                </>
              )}
              
              <InfoRow 
                label="Data Source" 
                value="Google Places API" 
              />
            </div>
          </div>
        )}

        {/* Google Places Types - spans full width if there are types */}
        {!statsLoading && businessDetails.google_place_id && stats.googlePlacesTypes.length > 0 && (
          <div className="col-span-full space-y-3 mt-6 pt-6 border-t border-border">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">
              Google Maps Categories & Types
            </h4>
            <div className="flex flex-wrap gap-2">
              {stats.googlePlacesTypes.map((type) => (
                <span key={type} className="px-3 py-1 text-xs bg-green-100 dark:bg-green-950/30 text-green-800 dark:text-green-300 rounded-full border border-green-200 dark:border-green-900">
                  {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              ))}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              These categories are automatically fetched from Google Maps based on your business listing.
            </div>
          </div>
        )}

        {/* Initial Google Data Baseline - shows when baseline data exists */}
        {businessDetails.initial_google_data_captured_at && (
          <div className="col-span-full space-y-3 mt-6 pt-6 border-t border-border">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">
              Growth Impact Tracking
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg border border-blue-200 dark:border-blue-900">
                <h5 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">Baseline (At Start)</h5>
                <div className="space-y-1">
                  <InfoRow 
                    label="Reviews" 
                    value={businessDetails.initial_google_review_count ?? 'N/A'}
                    labelClassName="text-blue-700 dark:text-blue-400 text-xs"
                    valueClassName="text-blue-800 dark:text-blue-300 font-medium text-xs"
                  />
                  <InfoRow 
                    label="Rating" 
                    value={businessDetails.initial_google_rating ? `${businessDetails.initial_google_rating.toFixed(1)}/5` : 'N/A'}
                    labelClassName="text-blue-700 dark:text-blue-400 text-xs"
                    valueClassName="text-blue-800 dark:text-blue-300 font-medium text-xs"
                  />
                </div>
              </div>
              
              <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg border border-green-200 dark:border-green-900">
                <h5 className="text-sm font-medium text-green-900 dark:text-green-200 mb-2">Current Status</h5>
                <div className="space-y-1">
                  <InfoRow 
                    label="Reviews" 
                    value={businessDetails.total_reviews ?? 'N/A'}
                    labelClassName="text-green-700 dark:text-green-400 text-xs"
                    valueClassName="text-green-800 dark:text-green-200 font-medium text-xs"
                  />
                  <InfoRow 
                    label="Rating" 
                    value={businessDetails.average_rating ? `${businessDetails.average_rating.toFixed(1)}/5` : 'N/A'}
                    labelClassName="text-green-700 dark:text-green-400 text-xs"
                    valueClassName="text-green-800 dark:text-green-200 font-medium text-xs"
                  />
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-3 text-center">
              Baseline captured on {new Date(businessDetails.initial_google_data_captured_at).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })} • Track your business growth over time
            </div>
          </div>
        )}
      </div>
    </SettingsCard>
  );
};